/**
 * placementStack — pure helpers for the undo-capable placement model.
 *
 * Instead of a flat Record<TerritoryId, count> that mutates in-place,
 * the placement phase now tracks a chronological array of single-step
 * actions.  The derived pending-counts map is recomputed by reducing
 * the stack, which makes "undo last step" trivial: pop the tail entry
 * and re-reduce.
 *
 * All functions are pure (no side effects, no React imports) so the
 * core logic can be tested in isolation from the component tree.
 */

export interface PlacementAction {
  territoryId: string
  armies: number
}

/**
 * Reduce a stack of placement actions into a territory→armies map.
 * Armies for the same territory across multiple taps are summed.
 */
export function derivePendingCounts(
  stack: PlacementAction[],
): Record<string, number> {
  const out: Record<string, number> = {}
  for (const action of stack) {
    out[action.territoryId] = (out[action.territoryId] ?? 0) + action.armies
  }
  return out
}

/**
 * Sum all armies across every action in the stack.
 * Equivalent to Object.values(derivePendingCounts(stack)).reduce(…, 0)
 * but O(n) without the intermediate object allocation.
 */
export function totalStackArmies(stack: PlacementAction[]): number {
  return stack.reduce((sum, a) => sum + a.armies, 0)
}

/**
 * Collapse the stack into a placements list suitable for the API:
 * one entry per territory, armies summed across all taps.
 * Entries with 0 armies are omitted (shouldn't normally occur but
 * guards against edge cases).
 */
export function stackToPlacementList(
  stack: PlacementAction[],
): Array<{ territoryId: string; armies: number }> {
  const counts = derivePendingCounts(stack)
  return Object.entries(counts)
    .filter(([, armies]) => armies > 0)
    .map(([territoryId, armies]) => ({ territoryId, armies }))
}
